import React, { useState, useEffect, useMemo, useRef } from "react";
import { useMediaQuery, useTheme, Box, Skeleton } from "@mui/material";
import { useTranslation } from "react-i18next";
import { CheckCircle, AccessTime, HelpOutline } from "@mui/icons-material";

// Constants
const CONSTANTS = {
  STOP_RADIUS: 50, // meters
  STOP_VISIT_TIME: 10, // seconds
  DEPARTURE_DISTANCE: 50, // meters
  LOOK_AHEAD_DISTANCE: 2000, // meters
  DATA_STALENESS_THRESHOLD: 30000, // ms
  EARTH_RADIUS: 6371e3, // meters
  MAX_STOP_DISTANCE: 4000, // meters
  MIN_STOP_DISTANCE: 200, // meters
  AVG_SPEED: 25, // km/h
  MIN_STOP_TIME: 60, // seconds
  MAX_STOP_TIME: 900, // seconds
  TRAFFIC_MULTIPLIER: 1.1, // 10% buffer
  ARRIVAL_DISTANCE: 70, // meters - distance threshold to show "Arrived"
  ARRIVAL_TIME: 10, // seconds - ETA threshold to show "Arrived"
};

const calculateRealisticETA = (distance) => {
  const distanceInKm = distance / 1000;
  let baseTime = (distanceInKm / CONSTANTS.AVG_SPEED) * 3600;
  baseTime *= CONSTANTS.TRAFFIC_MULTIPLIER;
  const numTrafficLights = Math.floor(distance / 800);
  const avgTrafficLightDelay = 20;
  baseTime += numTrafficLights * avgTrafficLightDelay;
  return Math.min(
    Math.max(Math.round(baseTime), CONSTANTS.MIN_STOP_TIME),
    CONSTANTS.MAX_STOP_TIME,
  );
};

const calculateImprovedDistance = (pos1, pos2) => {
  if (!pos1?.lat || !pos1?.lng || !pos2?.latitude || !pos2?.longitude)
    return Infinity;
  try {
    const φ1 = (pos1.lat * Math.PI) / 180;
    const φ2 = (parseFloat(pos2.latitude) * Math.PI) / 180;
    const Δφ = ((parseFloat(pos2.latitude) - pos1.lat) * Math.PI) / 180;
    const Δλ = ((parseFloat(pos2.longitude) - pos1.lng) * Math.PI) / 180;
    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = CONSTANTS.EARTH_RADIUS * c;
    return Math.min(
      Math.max(distance, CONSTANTS.MIN_STOP_DISTANCE),
      CONSTANTS.MAX_STOP_DISTANCE,
    );
  } catch (error) {
    console.error("Error calculating distance:", error);
    return Infinity;
  }
};

const calculateDistance = (pos1, pos2) => {
  if (!pos1?.lat || !pos1?.lng || !pos2?.latitude || !pos2?.longitude)
    return Infinity;
  try {
    const φ1 = (pos1.lat * Math.PI) / 180;
    const φ2 = (parseFloat(pos2.latitude) * Math.PI) / 180;
    const Δφ = ((parseFloat(pos2.latitude) - pos1.lat) * Math.PI) / 180;
    const Δλ = ((parseFloat(pos2.longitude) - pos1.lng) * Math.PI) / 180;
    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return CONSTANTS.EARTH_RADIUS * c;
  } catch (error) {
    console.error("Error calculating distance:", error);
    return Infinity;
  }
};

const getBestMetrics = (
  calculatedDistance,
  backendDistance,
  calculatedETA,
  backendETA,
) => {
  let finalDistance = calculatedDistance;
  let finalETA = calculatedETA;
  if (backendDistance) {
    const backendInMeters =
      backendDistance > 100 ? backendDistance : backendDistance * 1000;
    if (calculatedDistance && backendInMeters) {
      const diff = Math.abs(calculatedDistance - backendInMeters);
      if (diff / calculatedDistance < 0.5) {
        finalDistance = Math.min(calculatedDistance, backendInMeters);
      }
    }
  }
  if (backendETA && calculatedETA) {
    const diff = Math.abs(calculatedETA - backendETA);
    if (diff / calculatedETA < 0.5) {
      finalETA = Math.min(calculatedETA, backendETA);
    }
  }
  return {
    distance: finalDistance / 1000, // Convert to km
    eta: finalETA,
  };
};

const formatTime = (seconds) => {
  if (!seconds || seconds <= 0 || isNaN(seconds)) return null;
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${secs}s`;
  return `${secs}s`;
};

const TableCellSkeleton = ({ width = "70%" }) => (
  <Skeleton
    variant="rounded"
    width={width}
    height={22}
    sx={{ bgcolor: "#E7EEF7", borderRadius: "6px" }}
  />
);

// Load stop history from localStorage
const loadStopHistory = (vehicleId, scheduleDate) => {
  const key = `stopHistory_${vehicleId}_${scheduleDate}`;
  try {
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : {};
  } catch (error) {
    console.error("Error loading stop history:", error);
    return {};
  }
};

// Save stop history to localStorage
const saveStopHistory = (vehicleId, scheduleDate, history) => {
  const key = `stopHistory_${vehicleId}_${scheduleDate}`;
  try {
    localStorage.setItem(key, JSON.stringify(history));
  } catch (error) {
    console.error("Error saving stop history:", error);
  }
};

// Clear old history
const clearOldHistory = (vehicleId, currentScheduleDate) => {
  const prefix = `stopHistory_${vehicleId}_`;
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith(prefix) && !key.endsWith(currentScheduleDate)) {
      localStorage.removeItem(key);
    }
  });
};

// Calculate stop status
const calculateStopStatus = (
  stop,
  vehiclePosition,
  isMoving,
  stoppedDuration,
  pathDirection,
  stops,
) => {
  if (!vehiclePosition || !vehiclePosition.lat || !vehiclePosition.lng) {
    return {
      status: "No Location",
      tooltip: "Vehicle location unavailable",
      icon: <HelpOutline style={{ color: "gray" }} />,
    };
  }

  const stopPosition = {
    lat: parseFloat(stop.latitude),
    lng: parseFloat(stop.longitude),
  };
  const distance = calculateDistance(
    { latitude: vehiclePosition.lat, longitude: vehiclePosition.lng },
    stopPosition,
  );
  const isNearStop = distance <= 100; // 100m radius
  const isStoppedAtStop = isNearStop && !isMoving && stoppedDuration >= 30; // Stopped for 30+ seconds
  const currentTime = Date.now();

  // Check if this is the destination (source_to_dest) or source (dest_to_source)
  const isDestination =
    pathDirection === "source_to_dest" &&
    stop.name === stops[stops.length - 1].name;
  const isSource =
    pathDirection === "dest_to_source" && stop.name === stops[0].name;

  if ((isDestination || isSource) && isStoppedAtStop) {
    return {
      status: "Journey Complete",
      tooltip: `Reached ${isDestination ? "destination" : "source"} ${
        stop.name
      } at ${new Date(currentTime).toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
      })}`,
      icon: <CheckCircle style={{ color: "green" }} />,
      distance,
      timestamp: currentTime,
      journeyComplete: true,
    };
  }

  if (isStoppedAtStop) {
    return {
      status: "Reached",
      tooltip: `Stopped ${Math.round(distance)}m from ${
        stop.name
      } at ${new Date(currentTime).toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
      })}`,
      icon: <CheckCircle style={{ color: "green" }} />,
      distance,
      timestamp: currentTime,
    };
  }

  return {
    status: "Not Reached",
    tooltip: `Not yet reached ${stop.name}`,
    icon: <AccessTime style={{ color: "gray" }} />,
  };
};

const StopsTable = ({
  stops,
  stopDetails,
  vehiclePosition,
  isMoving,
  stoppedDuration,
  vehicleId,
  pathDirection,
  routeCoordinates,
  isInbound,
  loading = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [stopHistory, setStopHistory] = useState({});
  const [visitedStops, setVisitedStops] = useState(new Set());
  const [stopVisitTimer, setStopVisitTimer] = useState(null);
  const [lastStopDistance, setLastStopDistance] = useState(null);
  const [lastVisitedStop, setLastVisitedStop] = useState(null);
  const [previousData, setPreviousData] = useState({
    nextStop: null,
    stopDetail: null,
    lastUpdateTime: null,
  });

  const scheduleDate = "2025-05-08";

  // Refs to prevent infinite loops
  const prevPathDirectionRef = useRef(pathDirection);
  const stopsRef = useRef(stops);
  const vehicleIdRef = useRef(vehicleId);
  const pathDirectionRef = useRef(pathDirection);

  // Keep refs updated
  useEffect(() => {
    stopsRef.current = stops;
    vehicleIdRef.current = vehicleId;
    pathDirectionRef.current = pathDirection;
  });

  // Load stop history on mount
  useEffect(() => {
    if (vehicleId) {
      const history = loadStopHistory(vehicleId, scheduleDate);
      setStopHistory(history);
      clearOldHistory(vehicleId, scheduleDate);
    }
  }, [vehicleId, scheduleDate]);

  // Reset visited stops on direction change
  useEffect(() => {
    setVisitedStops(new Set());
    setStopVisitTimer(null);
    setLastStopDistance(null);
    setLastVisitedStop(null);
    setPreviousData({
      nextStop: null,
      stopDetail: null,
      lastUpdateTime: null,
    });
  }, [isInbound]);

  // Find route index for position
  const findRouteIndex = (position) => {
    if (!position || !routeCoordinates?.length) return -1;
    let minDistance = Infinity;
    let closestIndex = -1;
    routeCoordinates.forEach((coord, index) => {
      const distance = calculateDistance(position, {
        latitude: coord.latitude,
        longitude: coord.longitude,
      });
      if (distance < minDistance) {
        minDistance = distance;
        closestIndex = index;
      }
    });
    return closestIndex;
  };

  // Compute next stop
  const { nextStop } = useMemo(() => {
    try {
      if (!vehiclePosition || !stops?.length || !routeCoordinates?.length) {
        return previousData;
      }

      const currentIndex = findRouteIndex(vehiclePosition);
      if (currentIndex === -1) return previousData;

      // Check distance from last visited stop
      let distanceFromLastStop = Infinity;
      if (lastVisitedStop) {
        distanceFromLastStop = calculateImprovedDistance(
          vehiclePosition,
          lastVisitedStop,
        );
        setLastStopDistance(distanceFromLastStop);
      }

      if (distanceFromLastStop < CONSTANTS.DEPARTURE_DISTANCE) {
        return previousData;
      }

      // Find unvisited stops
      const stopsWithData = stops
        .filter((stop) => !visitedStops.has(stop.stopId))
        .map((stop) => ({
          ...stop,
          routeIndex: findRouteIndex({
            lat: parseFloat(stop.latitude),
            lng: parseFloat(stop.longitude),
          }),
          distance: calculateImprovedDistance(vehiclePosition, stop),
        }))
        .filter((stop) => stop.routeIndex !== -1);

      const validStops = stopsWithData
        .filter((stop) =>
          isInbound
            ? stop.routeIndex <= currentIndex
            : stop.routeIndex >= currentIndex,
        )
        .sort((a, b) =>
          isInbound ? b.routeIndex - a.routeIndex : a.routeIndex - b.routeIndex,
        );

      if (!validStops.length) return previousData;

      const nextValidStop = validStops[0];
      const backendStopDetail = stopDetails[nextValidStop.stopId];
      const calculatedDistance = nextValidStop.distance;
      const calculatedETA = calculateRealisticETA(calculatedDistance);
      const bestMetrics = getBestMetrics(
        calculatedDistance,
        backendStopDetail?.distance,
        calculatedETA,
        backendStopDetail?.eta,
      );

      return {
        nextStop: nextValidStop,
        stopDetail: {
          ...backendStopDetail,
          distance: bestMetrics.distance,
          eta: bestMetrics.eta,
        },
      };
    } catch (error) {
      console.error("Error determining next stop:", error);
      return previousData;
    }
  }, [
    vehiclePosition,
    stops,
    stopDetails,
    routeCoordinates,
    isInbound,
    visitedStops,
    lastVisitedStop,
    lastStopDistance,
    previousData,
  ]);

  // Update previous data
  useEffect(() => {
    if (nextStop) {
      setPreviousData({
        nextStop,
        stopDetail: stopDetails[nextStop.stopId],
        lastUpdateTime: Date.now(),
      });
    }
  }, [nextStop?.stopId, stopDetails]);

  // Track stop visits
  useEffect(() => {
    if (!vehiclePosition || !stops?.length) return;
    stops.forEach((stop) => {
      const distance = calculateDistance(vehiclePosition, stop);
      if (distance <= CONSTANTS.STOP_RADIUS) {
        if (!stopVisitTimer && !visitedStops.has(stop.stopId)) {
          setStopVisitTimer({
            stopId: stop.stopId,
            startTime: Date.now(),
          });
        } else if (stopVisitTimer?.stopId === stop.stopId) {
          const timeSpent = (Date.now() - stopVisitTimer.startTime) / 1000;
          if (timeSpent >= CONSTANTS.STOP_VISIT_TIME) {
            setVisitedStops((prev) => new Set([...prev, stop.stopId]));
            setLastVisitedStop(stop);
            setStopVisitTimer(null);
          }
        }
      } else if (stopVisitTimer?.stopId === stop.stopId) {
        setStopVisitTimer(null);
      }
    });
  }, [vehiclePosition, stops, visitedStops, stopVisitTimer]);

  // Group stops by name and keep the closest one
  const uniqueStops = useMemo(() => {
    return stops.reduce((acc, stop) => {
      const distA = stopDetails[stop.stopId]?.distance;
      const numA = typeof distA === "number" ? distA : parseFloat(distA);
      if (!acc[stop.name]) {
        acc[stop.name] = stop;
        return acc;
      }
      const existingDist = stopDetails[acc[stop.name].stopId]?.distance;
      const numExisting =
        typeof existingDist === "number"
          ? existingDist
          : parseFloat(existingDist);
      if (
        !isNaN(numA) &&
        (!numExisting || isNaN(numExisting) || numA < numExisting)
      ) {
        acc[stop.name] = stop;
      }
      return acc;
    }, {});
  }, [stops, stopDetails]);

  // Derive allStops from uniqueStops
  const allStops = useMemo(() => {
    return Object.values(uniqueStops).map((stop) => stop.name);
  }, [uniqueStops]);

  // FIX 1: Stop History Update - Add proper comparison and dependencies
  useEffect(() => {
    if (
      !vehiclePosition ||
      !vehicleIdRef.current ||
      stopsRef.current.length === 0
    )
      return;

    const checkAndUpdate = () => {
      let hasChanges = false;
      const updates = {};

      stopsRef.current.forEach((stop) => {
        const status = calculateStopStatus(
          stop,
          vehiclePosition,
          isMoving,
          stoppedDuration,
          pathDirectionRef.current,
          stopsRef.current,
        );

        if (
          status.status === "Reached" ||
          status.status === "Journey Complete"
        ) {
          const currentStatus = stopHistory[stop.name]?.status;
          const currentTimestamp = stopHistory[stop.name]?.timestamp;

          // Only update if status changed OR it's a new entry
          if (
            !currentStatus ||
            currentStatus !== status.status ||
            (!currentTimestamp && status.timestamp)
          ) {
            updates[stop.name] = {
              status: status.status,
              tooltip: status.tooltip,
              distance: status.distance,
              timestamp: status.timestamp,
            };
            hasChanges = true;
          }
        }
      });

      if (hasChanges && Object.keys(updates).length > 0) {
        setStopHistory((prev) => {
          // Check if updates are actually different
          const isDifferent = Object.keys(updates).some((key) => {
            const existing = prev[key];
            const update = updates[key];
            return (
              !existing ||
              existing.status !== update.status ||
              existing.timestamp !== update.timestamp
            );
          });

          if (!isDifferent) return prev; // Return same reference to prevent re-render

          const newHistory = { ...prev, ...updates };
          saveStopHistory(vehicleIdRef.current, scheduleDate, newHistory);
          return newHistory;
        });
      }
    };

    // Use requestAnimationFrame to avoid rapid updates
    const rafId = requestAnimationFrame(checkAndUpdate);

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [
    vehiclePosition?.lat,
    vehiclePosition?.lng,
    isMoving,
    stoppedDuration,
    scheduleDate,
  ]);

  // FIX 2: PathDirection Reset - Use stable comparison
  useEffect(() => {
    // Only run when pathDirection actually changes
    if (!pathDirection || prevPathDirectionRef.current === pathDirection) {
      return;
    }

    prevPathDirectionRef.current = pathDirection;

    if (!vehicleId || !stopsRef.current.length) return;

    const resetHistory = {};
    stopsRef.current.forEach((stop) => {
      resetHistory[stop.name] = {
        status: "Not Reached",
        tooltip: `New journey started in ${pathDirection} direction`,
        distance: null,
        timestamp: Date.now(),
      };
    });

    setStopHistory(resetHistory);
    saveStopHistory(vehicleId, scheduleDate, resetHistory);
  }, [pathDirection, vehicleId, scheduleDate]);
  // ← Remove 'stops' from dependencies, use stopsRef.current instead

  // Sort stops for rendering: nextStop first, then upcoming stops, then passed stops
  // const sortedStops = useMemo(() => {
  //   const order =
  //     pathDirection === "dest_to_source" ? [...allStops].reverse() : allStops;
  //   const stopsArray = Object.values(uniqueStops);

  //   if (!nextStop) {
  //     // If no nextStop, return all stops in journey order
  //     return stopsArray.sort(
  //       (a, b) => order.indexOf(a.name) - order.indexOf(b.name)
  //     );
  //   }

  //   const nextStopIndex = order.indexOf(nextStop.name);

  //   // Get nextStop object
  //   const nextStopObj =
  //     stopsArray.find((stop) => stop.stopId === nextStop.stopId) || nextStop;

  //   // Get stops that come AFTER the next stop in the journey order (upcoming)
  //   const upcomingStops = stopsArray
  //     .filter((stop) => {
  //       const stopIndex = order.indexOf(stop.name);
  //       return stop.stopId !== nextStop.stopId && stopIndex > nextStopIndex;
  //     })
  //     .sort((a, b) => order.indexOf(a.name) - order.indexOf(b.name));

  //   // Get stops that come BEFORE the next stop in the journey order (passed)
  //   const passedStops = stopsArray
  //     .filter((stop) => {
  //       const stopIndex = order.indexOf(stop.name);
  //       return stop.stopId !== nextStop.stopId && stopIndex < nextStopIndex;
  //     })
  //     .sort((a, b) => order.indexOf(a.name) - order.indexOf(b.name));

  //   // Return nextStop first, then upcoming stops, then passed stops
  //   return [nextStopObj, ...upcomingStops, ...passedStops];
  // }, [nextStop, uniqueStops, allStops, pathDirection]);
  const sortedStops = useMemo(() => {
    const order =
      pathDirection === "dest_to_source" ? [...allStops].reverse() : allStops;
    return Object.values(uniqueStops).sort(
      (a, b) => order.indexOf(a.name) - order.indexOf(b.name),
    );
  }, [uniqueStops, allStops, pathDirection]);

  // Function to get row background color based on position relative to nextStop
  const getRowBackgroundColor = (stopName) => {
    if (!nextStop) return "#ffffff"; // Default white if no nextStop
    const order =
      pathDirection === "dest_to_source" ? [...allStops].reverse() : allStops;
    const nextStopIndex = order.indexOf(nextStop.name);
    const stopIndex = order.indexOf(stopName);
    return stopIndex >= nextStopIndex ? "#ffe5e5" : "#eafbea";
  };

  const renderDistance = (stop) => {
    if (vehiclePosition && stop) {
      // Use unclamped distance for arrival check
      const rawDistance = calculateDistance(vehiclePosition, stop);
      if (rawDistance !== Infinity) {
        const liveETA = calculateRealisticETA(rawDistance);
        if (rawDistance < CONSTANTS.ARRIVAL_DISTANCE && liveETA <= CONSTANTS.ARRIVAL_TIME) {
          return (
            <span style={{
              backgroundColor: "#2e7d32",
              color: "#fff",
              borderRadius: "12px",
              padding: "2px 10px",
              fontSize: "12px",
              fontWeight: 600,
            }}>Arrived</span>
          );
        }
        return `${(rawDistance / 1000).toFixed(1)} km`;
      }
    }

    // Fallback to stopDetails
    const details = stopDetails[stop.stopId];
    if (details?.distance && !isNaN(parseFloat(details.distance))) {
      return `${parseFloat(details.distance).toFixed(1)} km`;
    }

    return <TableCellSkeleton width="42%" />;
  };

  const renderETA = (stop) => {
    if (vehiclePosition && stop) {
      // Use unclamped distance for arrival check
      const rawDistance = calculateDistance(vehiclePosition, stop);
      if (rawDistance !== Infinity) {
        const liveETA = calculateRealisticETA(rawDistance);
        if (rawDistance < CONSTANTS.ARRIVAL_DISTANCE && liveETA <= CONSTANTS.ARRIVAL_TIME) {
          return (
            <span style={{
              backgroundColor: "#2e7d32",
              color: "#fff",
              borderRadius: "12px",
              padding: "2px 10px",
              fontSize: "12px",
              fontWeight: 600,
            }}>Arrived</span>
          );
        }
        const formattedTime = formatTime(liveETA);
        if (formattedTime) return formattedTime;
      }
    }

    // Fallback to stopDetails
    const details = stopDetails[stop.stopId];
    if (details?.eta && !isNaN(details.eta) && details.eta !== 0) {
      const formattedTime = formatTime(details.eta);
      if (formattedTime) return formattedTime;
    }

    return <TableCellSkeleton width="36%" />;
  };

  const skeletonRows = Array.from({ length: 5 }, (_, index) => index);

  // Mobile card view
  if (isMobile) {
    if (loading) {
      return (
        <Box sx={{ width: "100%" }}>
          {skeletonRows.map((row) => (
            <Box
              key={row}
              sx={{
                padding: "16px",
                backgroundColor: "#FFFFFF",
                borderBottom: "1px solid #ddd",
              }}
            >
              <TableCellSkeleton width="48%" />
              <Box sx={{ mt: 1.5, display: "flex", justifyContent: "space-between" }}>
                <TableCellSkeleton width="30%" />
                <TableCellSkeleton width="24%" />
              </Box>
              <Box sx={{ mt: 1, display: "flex", justifyContent: "space-between" }}>
                <TableCellSkeleton width="22%" />
                <TableCellSkeleton width="20%" />
              </Box>
            </Box>
          ))}
        </Box>
      );
    }

    return (
      <Box sx={{ width: "100%" }}>
        {sortedStops.map((stop, index) => {
          const backgroundColor = getRowBackgroundColor(stop.name);
          return (
            <Box
              key={stop.stopId}
              sx={{
                padding: "16px",
                backgroundColor: backgroundColor,
                borderBottom: "1px solid #ddd",
                borderRadius:
                  index === 0
                    ? "4px 4px 0 0"
                    : index === sortedStops.length - 1
                      ? "0 0 4px 4px"
                      : "0",
              }}
            >
              <Box
                sx={{ fontWeight: 600, fontSize: "16px", marginBottom: "8px" }}
              >
                {stop.name}
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "4px",
                }}
              >
                <Box sx={{ color: "#666", fontSize: "14px" }}>
                  {t("Distance")}:
                </Box>
                <Box sx={{ fontSize: "14px" }}>{renderDistance(stop)}</Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "4px",
                }}
              >
                <Box sx={{ color: "#666", fontSize: "14px" }}>{t("ETA")}:</Box>
                <Box sx={{ fontSize: "14px" }}>{renderETA(stop)}</Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        borderRadius: "10px",
        display: "flex",
        justifyContent: "space-between",
        padding: "16px",
        backgroundColor: "#FFFFFF",
        border: "1px solid #A5DCF3",
        boxShadow: "-2px 1px 5px 1px #00000040",
      }}
    >
      <table
        style={{
          width: "100%",
          borderCollapse: "separate",
          borderSpacing: 0,
          minWidth: "auto",
          tableLayout: "fixed",
        }}
      >
        <thead
          style={{
            boxShadow: "-2px 2px 5px 0px #00000040",
            borderRadius: "8px",
          }}
        >
          <tr>
            <th
              style={{
                ...tableHeaderStyle,
                width: "10px",
                borderTopLeftRadius: "8px",
                borderBottomLeftRadius: "8px",
              }}
            ></th>
            <th style={tableHeaderStyle}>{t("Stops")}</th>
            <th style={tableHeaderStyle}>{t("Distance")}</th>
            <th
              style={{
                ...tableHeaderStyle,
                borderTopRightRadius: "8px",
                borderBottomRightRadius: "8px",
              }}
            >
              {t("ETA")}
            </th>
          </tr>
        </thead>
        <tbody>
          {loading
            ? skeletonRows.map((row) => (
                <tr key={row} style={{ backgroundColor: "#FFFFFF" }}>
                  <td style={tableCellStyle}></td>
                  <td style={tableCellStyle}>
                    <TableCellSkeleton width="62%" />
                  </td>
                  <td style={tableCellStyle}>
                    <TableCellSkeleton width="42%" />
                  </td>
                  <td style={tableCellStyle}>
                    <TableCellSkeleton width="36%" />
                  </td>
                </tr>
              ))
            : sortedStops.map((stop) => {
                const backgroundColor = getRowBackgroundColor(stop.name);
                return (
                  <tr
                    key={stop.stopId}
                    style={{
                      fontSize: "16px",
                      color: "#1F242E",
                      backgroundColor: backgroundColor,
                    }}
                  >
                    <td style={tableCellStyle}></td>
                    <td style={tableCellStyle}>{stop.name}</td>
                    <td style={tableCellStyle}>{renderDistance(stop)}</td>
                    <td style={tableCellStyle}>{renderETA(stop)}</td>
                  </tr>
                );
              })}
        </tbody>
      </table>
    </Box>
  );
};

const tableHeaderStyle = {
  backgroundColor: "#647490",
  color: "white",
  fontSize: "16px",
  fontWeight: "600",
  textAlign: "left",
  padding: "12px 20px",
  borderBottom: "2px solid #ddd",
};

const tableCellStyle = {
  padding: "12px 20px",
  textAlign: "left",
  fontSize: "14px",
  borderBottom: "1px solid #ddd",
  color: "#333",
};

export default StopsTable;
